import {Button, Form, Input, Modal, Select, message} from 'antd';
import PropTypes from 'prop-types';
import React, {useEffect} from 'react';

import {RQUpdateUser} from '../../../../../ReactQuery/apis';

const GENDER_OPTIONS = [
  {value: 'male', label: 'Male'},
  {value: 'female', label: 'Female'},
  {value: 'other', label: 'Other'},
];

const STATUS_OPTIONS = ['ACTIVE', 'INACTIVE'];

const BLOOD_GROUP_OPTIONS = [
  'A+',
  'A-',
  'B+',
  'B-',
  'AB+',
  'AB-',
  'O+',
  'O-',
];

const MARITAL_STATUS_OPTIONS = ['MARRIED', 'UNMARRIED'];

// Matches RegisterPatient's field set exactly (minus password, which isn't
// something you edit through a generic "update details" form).
const buildUpdatePayload = values => ({
  fullName: values.fullName,
  email: values.email,
  mobileNumber: values.mobileNumber,
  age: values.age,
  gender: values.gender,
  bloodGroup: values.bloodGroup,
  occupation: values.occupation,
  maritalStatus: values.maritalStatus,
  status: values.status,
  address: values.address,
});

export const EditUser = ({open, onClose, onSuccess, user, token}) => {
  const [form] = Form.useForm();

  const updateUser = RQUpdateUser({
    onSuccess: () => {
      message.success('Patient updated successfully');
      onSuccess?.();
      onClose();
    },
    onError: err => {
      message.error(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to update patient',
      );
    },
  });

  useEffect(() => {
    if (open && user) {
      form.setFieldsValue({
        fullName: user.fullName,
        email: user.email,
        mobileNumber: user.mobileNumber,
        age: user.age,
        gender: user.gender,
        bloodGroup: user.bloodGroup,
        occupation: user.occupation,
        maritalStatus: user.maritalStatus,
        status: user.status,
        address: user.address,
      });
    }
  }, [open, user, form]);

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const handleFinish = values => {
    if (!user?.id) {
      message.error('Missing user id, cannot update');

      return;
    }

    updateUser.mutate({
      id: user.id,
      formData: buildUpdatePayload(values),
      token,
    });
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      destroyOnHidden
      width={680}
      title={(
        <span className={'text-primary-700 font-bold text-base'}>
          Edit Patient
        </span>
      )}
    >
      <Form form={form} layout={'vertical'} onFinish={handleFinish} className={'mt-4'}>
        <div className={'grid grid-cols-1 md:grid-cols-2 gap-x-4'}>
          <Form.Item
            label={'Full Name'}
            name={'fullName'}
            rules={[
              {
                required: true,
                message: 'Please enter full name',
              },
              {
                validator: (_, value) => {
                  if (!value) {return Promise.resolve();}

                  if (value.startsWith(' ')) {
                    return Promise.reject(
                      new Error('First character cannot be a space'),
                    );
                  }

                  if (!/^[A-Za-z ]+$/.test(value)) {
                    return Promise.reject(
                      new Error('Full name can contain only letters and spaces'),
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              placeholder={'e.g. John Doe'}
              onKeyPress={e => {
                if (!/[A-Za-z ]/.test(e.key)) {
                  e.preventDefault();
                }
              }}
            />
          </Form.Item>

          <Form.Item
            label={'Mobile Number'}
            name={'mobileNumber'}
            rules={[
              {required: true, message: 'Please enter mobile number'},
              {pattern: /^[0-9]{10}$/, message: 'Enter a valid 10-digit mobile number'},
            ]}
          >
            <Input placeholder={'9876543210'} maxLength={10} />
          </Form.Item>

          <Form.Item
            label={'Age'}
            name={'age'}
            rules={[
              {
                required: true,
                message: 'Please enter age',
              },
              {
                pattern: /^[0-9]+$/,
                message: 'Age must contain only numbers',
              },
              {
                validator: (_, value) => {
                  if (!value) {return Promise.resolve();}
                  const age = Number(value);
                  if (age < 1 || age > 120) {
                    return Promise.reject(new Error('Age must be between 1 and 120'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              placeholder={'32'}
              maxLength={3}
              onKeyPress={e => {
                if (!/[0-9]/.test(e.key)) {
                  e.preventDefault();
                }
              }}
            />
          </Form.Item>
          <Form.Item
            label={'Gender'}
            name={'gender'}
            rules={[{required: true, message: 'Please select gender'}]}
          >
            <Select placeholder={'Select Gender'} options={GENDER_OPTIONS} />
          </Form.Item>

          <Form.Item label={'Blood Group'} name={'bloodGroup'}>
            <Select
              placeholder={'Select Blood Group'}
              allowClear
              options={BLOOD_GROUP_OPTIONS.map(item => ({value: item, label: item}))}
            />
          </Form.Item>

          <Form.Item label={'Occupation'} name={'occupation'}>
            <Input placeholder={'Engineer'} />
          </Form.Item>

          <Form.Item label={'Marital Status'} name={'maritalStatus'}>
            <Select
              placeholder={'Select Marital Status'}
              allowClear
              options={MARITAL_STATUS_OPTIONS.map(item => ({value: item, label: item}))}
            />
          </Form.Item>

          <Form.Item label={'Status'} name={'status'}>
            <Select options={STATUS_OPTIONS.map(item => ({value: item, label: item}))} />
          </Form.Item>

          <Form.Item label={'Address'} name={'address'} className={'md:col-span-2'}>
            <Input.TextArea rows={3} placeholder={'Enter Address'} />
          </Form.Item>
        </div>

        <div className={'flex justify-end gap-2 mt-3'}>
          <Button onClick={handleCancel} className={'btn-secondary'}>
            Cancel
          </Button>

          <Button
            type={'primary'}
            htmlType={'submit'}
            loading={updateUser.isPending}
            className={'btn-primary'}
          >
            Save Changes
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

EditUser.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  user: PropTypes.object,
  token: PropTypes.string,
};

export default EditUser;
